import * as Sentry from '@sentry/browser';
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';
import makePrivateApiRequest from '../../utils/makePrivateApiRequest';
import { track } from '../../utils/tracking';

import { API_ENDPOINTS, PRIVATE_API_ENDPOINTS } from '../../constants';
import Eyebrow from './Eyebrow';
import Header from './Header';

const RETRY_LIMIT = 10;

const StepInstantActivation = ({ onFetchedOrder, code, steps, setStatus }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let interval;
    let checkCounter = 0;

    if (!code) {
      return;
    }

    track('activation_instant_start');

    if (code && checkCounter < RETRY_LIMIT) {
      interval = setInterval(async () => {
        checkCounter += 1;

        try {
          const response = await makeApiRequest(API_ENDPOINTS.ACTIVATION_BEGIN, { activationCode: code }, 'POST', true);

          if (response.Status && response.data.nextStep) {
            clearInterval(interval);

            const rules = await makePrivateApiRequest(PRIVATE_API_ENDPOINTS.ACTIVATION_RULES, { code });

            // Find the next step
            let nextStep;
            if (response.data.nextStep) {
              nextStep = response.data.nextStep;
            } else if (response.data.currentStep) {
              nextStep = response.data.currentStep;
            } else {
              const finishedStess = response.data.Steps.sort((a, b) => b.StepOrder - a.StepOrder);
              nextStep = finishedStess.find((step) => step.StepJson?.nextStep)?.StepJson.nextStep;
            }

            if (nextStep === steps.NUMBER_TRANSFER) {
              nextStep = steps.NUMBER_TRANSFER_STARTED;
            }

            // Check if new number is activated
            if (response.Steps?.some((step) => step.StepName === 'New' && step.StepJson?.ProvSyncTime)) {
              setStatus((current) => ({ ...current, provisioned: true }));
            }

            const results = {
              customerId: response.data.CustomerId ? response.data.CustomerId : response.data.Steps[0].CustomerID,
              orderId: code,
              rules,
              nextStep,
            };

            onFetchedOrder(results);
            track('activation_instant_success');
          }
        } catch (error) {
          setFailed(true);
          console.error(error);
          Sentry.captureException(error);
          track('activation_instant_failed');
        }

        if (checkCounter >= RETRY_LIMIT) {
          setFailed(true);
          clearInterval(interval);
          track('activation_instant_timeout');
          return;
        }
      }, 2500);
    }

    return () => clearInterval(interval);
  }, []);

  if (failed) {
    return html`
      <${Header}
        title="Still Working on Your eSIM - You'll Get an Email!"
        description="It looks like your eSIM isn't ready yet. We'll send you an email as soon as it's ready."
        eyebrow="Need A Few More Minutes"
        isNormalCase
      />
    `;
  }

  return html`
    <div class="flex flex-col items-center md:mb-8 text-center gap-3 mt-8 mb-5 max-w-md">
      <${Eyebrow} text="Only Takes 10 Seconds" />
      <h2 class="h2 leading-none relative self-center normal-case">Almost There - We're Creating Your eSIM!</h2>
    </div>
    <video autoplay muted loop width="400" height="325" class="rounded-4xl shadow-md">
      <source src="https://cdn.shopify.com/videos/c/o/v/2664788322284c41b7a29c1a26f8b583.mp4" type="video/mp4" />
    </video>
  `;
};

export default StepInstantActivation;
